import * as Sentry from "@sentry/react";
import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes, useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from "react-router-dom";
import "./index.css";
import App from "./App.tsx";
import Docs from "./Docs.tsx";
import NotFound from "./NotFound.tsx";
import PrivacyPolicy from "./PrivacyPolicy.tsx";
import UpgradeCancel from "./pages/redirects/UpgradeCancel";
import UpgradeSuccess from "./pages/redirects/UpgradeSuccess";
import Welcome from "./pages/redirects/Welcome";
import TermsOfService from "./TermsOfService.tsx";
import SentryErrorBoundary from "./components/SentryErrorBoundary";

function getEnvironment(): "local" | "staging" | "prod" {
  if (import.meta.env.DEV) return "local";
  const host = window.location.hostname;
  if (host.includes("staging") || host.endsWith(".pages.dev")) return "staging";
  return "prod";
}

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: getEnvironment(),
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
  });
}

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

createRoot(rootElement).render(
  <StrictMode>
    <SentryErrorBoundary>
      <BrowserRouter>
        <SentryRoutes>
          <Route path="/" element={<App />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/upgrade/success" element={<UpgradeSuccess />} />
          <Route path="/upgrade/cancel" element={<UpgradeCancel />} />
          <Route path="*" element={<NotFound />} />
        </SentryRoutes>
      </BrowserRouter>
    </SentryErrorBoundary>
  </StrictMode>
);
